import React from 'react'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import _ from 'lodash'
import BarChartIcon from '@mui/icons-material/BarChart'
import TableChartIcon from '@mui/icons-material/TableChart'
import { LooksOne, Percent } from '@mui/icons-material'
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts'
import ChartTooltip from '../../components/charts/ChartTooltip'
import { numberPrecision } from '../../helpers/UtilityHelper'
import useGetDayWiseData from './useGetDayWiseData'
import { TableSkeleton } from '../Skeletons'
import MaterialTable from '../MaterialTable'

const ATTEMPT_COLORS = {
  '0': '#A78BFA',
  '1': '#F5C05B',
  '2': '#8FD0C7',
  '3': '#F0A4B8',
  '4': '#B2D7A3',
  '5': '#FFB88E',
  '6': '#FC9E7F',
  '7': '#FC746F',
  '8': '#C23B3B',
  '8+': '#F4D03F',
  'N/A': '#F3B8CA',
}

export default function DayWiseAttempt({ data, title, groupOn, isRvp }) {
  const [view, setView] = React.useState('chart')
  const [tableView, setTableView] = React.useState('percentage')

  const { finalData, isLoading } = useGetDayWiseData(data, groupOn, isRvp)
  const allColumns = _.map(_.get(finalData, '0.split', []), 'name')

  const tableData = React.useMemo(() => {
    const transformData = finalData.map((item) => {
      const row = { ageing: item.ageing, count: item.count, percentage: item.percentage }

      for (const i of item.split) {
        row[i.name] = { percentage: i.percentage, count: i.count }
      }

      return row
    })

    return transformData
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(finalData)])


  const tabelColumns = React.useMemo(() => {
    return [
      { label: 'Attempt Day', value: 'ageing', align: 'left' },
      ...allColumns.map((col) => ({
        label: col,
        value: (row) =>
          `${numberPrecision(row[col][tableView], 1)}${tableView === 'percentage' ? '%' : ''}`,
        numeric: true,
        align: 'right',
      })),
      {
        label: 'Total',
        value: (row) =>
          `${numberPrecision(row[tableView], 1)}${tableView === 'percentage' ? '%' : ''}`,
        numeric: true,
        align: 'right',
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allColumns), JSON.stringify(tableView)])

  const chartData = React.useMemo(() => {
    return allColumns.map((dateName) => {
      const row = { name: dateName, _stackTotal: 0 }
      for (const ageingRow of finalData) {
        const split = ageingRow.split?.find((s) => s.name === dateName)
        const count = split?.count || 0
        row[`day_${ageingRow.ageing}`] = count
        row._stackTotal += count
      }
      return row
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allColumns), JSON.stringify(finalData)])

  const attemptKeys = finalData.map((r) => r.ageing)
  const chartMax = chartData.reduce((m, r) => Math.max(m, r._stackTotal || 0), 0) || 1
  const plotHeight = 380

  if (isLoading) {
    return <TableSkeleton size='large' />
  }

  return (
    <Card elevation={1}>
      <CardHeader
        title={title}
        subheader={
          <Typography variant='body2'>
            {isRvp
              ? 'The data below offers an overview of the number of days it has taken for the courier partner to attempt pickup for all pickedup shipments. The days are measured from the awb registered date and first pickup attempt.'
              : 'The data below offers an overview of the number of days it has taken for the courier partner to attempt delivery for all delivered shipments. The days are measured from the actual pickup date.'}
          </Typography>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup color='primary' size='small' exclusive onChange={(e, v) => { if (v) setView(v) }} value={view}>
              <ToggleButton value='chart' title='View Chart'>
                <BarChartIcon />
              </ToggleButton>
              <ToggleButton value='table' title='View Table'>
                <TableChartIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            {view === 'table' && (
              <ToggleButtonGroup
                color='primary'
                size='small'
                exclusive
                onChange={(e, v) => { if (v) setTableView(v) }}
                value={tableView}
              >
                <ToggleButton value='percentage' title='View Data In %'>
                  <Percent />
                </ToggleButton>
                <ToggleButton value='count' title='View Data In Count'>
                  <LooksOne />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
        }
      />
      <CardContent>
        {view === 'chart' && (
          <ResponsiveContainer width='100%' height={440}>
            <ComposedChart data={chartData} margin={{ top: 30, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray='3 3' stroke='#dee2e6' />
              <XAxis dataKey='name' fontSize='11px' fontWeight='500' />
              <YAxis fontSize='11px' fontWeight='500' label={{ value: '# Shipments', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#666', textAnchor: 'middle' }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend verticalAlign='top' align='center' iconSize='10px' iconType='circle' wrapperStyle={{ fontSize: 11, fontWeight: 500, paddingBottom: 10 }} />
              {attemptKeys.map((k, i) => {
                const color = ATTEMPT_COLORS[k] || '#999'
                const isLast = i === attemptKeys.length - 1
                return (
                  <Area
                    key={k}
                    dataKey={`day_${k}`}
                    name={`Attempt Day: ${k}`}
                    stackId='attempts'
                    type='monotone'
                    stroke={color}
                    fill={color}
                    fillOpacity={0.85}
                    strokeWidth={1}
                    animationEasing='ease-in-out'
                    animationDuration={1000}
                  >
                    <LabelList
                      dataKey={`day_${k}`}
                      content={({ x, y, value }) => {
                        if (!value || value === 0 || x == null || y == null) return null
                        const segPx = (value / chartMax) * plotHeight
                        if (segPx < 16) return null
                        return (
                          <text
                            x={x}
                            y={y + segPx / 2 + 3}
                            fill='rgba(0,0,0,0.55)'
                            fontSize={9}
                            fontWeight={500}
                            textAnchor='middle'
                          >
                            {value}
                          </text>
                        )
                      }}
                    />
                    {isLast && (
                      <LabelList dataKey='_stackTotal' position='top' style={{ fontSize: 10, fontWeight: 700, fill: '#333' }} formatter={(v) => (v == null || v === 0) ? '' : v} />
                    )}
                  </Area>
                )
              })}
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {view === 'table' && (
          <MaterialTable
            size='small'
            fileName={title}
            addBorders
            rows={tableData}
            columns={tabelColumns}
            frozenColumnsCount={1}
            exportExcel={false}
          />
        )}
      </CardContent>
    </Card>
  )
}
